import * as React from 'react'

import {
  ColumnDef,
  SortingState,
  VisibilityState,
  RowSelectionState,
  OnChangeFn,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  FilterFnOption,
  getFilteredRowModel,
  CellContext,
  HeaderContext,
} from '@tanstack/react-table'

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTranslation } from 'react-i18next'
import { Input } from './input'
import { useEffect } from 'react'
import { PulseLoader } from 'react-spinners'
import { TFunction } from 'i18next'
import { getErrorDetails } from 'app/frontend/base/helpers'
import { Checkbox } from './checkbox'
import { useTableSearchParams } from 'tanstack-table-search-params'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'

// Imported from https://gist.github.com/nicostombros/fc9a16a67fc47a536cf84736d0b8cfd8

type PaginatorProps = {
  currentPage: number
  totalPages: number
  onPageChange: (pageNumber: number) => void
  showPreviousNext: boolean
}

const generatePaginationLinks = (
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void,
) => {
  const pages: JSX.Element[] = []
  if (totalPages <= 6) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            className='h-8 w-8'
            onClick={() => onPageChange(i)}
            isActive={i === currentPage}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      )
    }
  } else {
    for (let i = 1; i <= 2; i++) {
      pages.push(
        <PaginationItem key={`left-${i}`}>
          <PaginationLink
            className='h-8 w-8'
            onClick={() => onPageChange(i)}
            isActive={i === currentPage}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      )
    }
    if (2 < currentPage && currentPage < totalPages - 1) {
      pages.push(<PaginationEllipsis key='first-ellipsis' />)
      pages.push(
        <PaginationItem key={`middle-${currentPage}`}>
          <PaginationLink
            className='h-8 w-8'
            onClick={() => onPageChange(currentPage)}
            isActive={true}
          >
            {currentPage}
          </PaginationLink>
        </PaginationItem>,
      )
    }
    pages.push(<PaginationEllipsis key='second-ellipsis' />)
    for (let i = totalPages - 1; i <= totalPages; i++) {
      pages.push(
        <PaginationItem key={`right-${i}`}>
          <PaginationLink
            className='h-8 w-8'
            onClick={() => onPageChange(i)}
            isActive={i === currentPage}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      )
    }
  }
  return pages
}

function TablePagination({
  currentPage,
  totalPages,
  onPageChange,
  showPreviousNext,
}: PaginatorProps) {
  if (totalPages === 1) {
    return null
  } else {
    return (
      <Pagination className='w-fit mx-0'>
        <PaginationContent className='flex-wrap'>
          {showPreviousNext && totalPages ? (
            <PaginationItem>
              <PaginationPrevious
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage - 1 < 1}
              />
            </PaginationItem>
          ) : null}
          {generatePaginationLinks(currentPage, totalPages, onPageChange)}
          {showPreviousNext && totalPages ? (
            <PaginationItem>
              <PaginationNext
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage > totalPages - 1}
              />
            </PaginationItem>
          ) : null}
        </PaginationContent>
      </Pagination>
    )
  }
}

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  className?: string
  rowClassName?: (row: TData) => string
  cellClassName?: string
  data: TData[]
  emptyMessage?: () => string | React.ReactNode
  visibilityState?: VisibilityState
  sortingState?: SortingState
  onVisibleRowsChange?: (newRows: TData[]) => void
  onRowClick?: (row: TData) => void
  pagination?: number
  filterType?: FilterFnOption<TData>
  includeHeader: boolean
  customHeaderActions?: React.ReactNode
  getRowId?: (row: TData) => string
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
}

function DataTable<TData, TValue>({
  columns,
  className,
  rowClassName,
  cellClassName,
  data,
  visibilityState,
  sortingState,
  emptyMessage,
  pagination,
  onVisibleRowsChange,
  customHeaderActions,
  onRowClick,
  filterType,
  includeHeader = true,
  getRowId,
  rowSelection,
  onRowSelectionChange,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const [sorting, setSorting] = React.useState<SortingState>(sortingState || [])

  const urlSyncConfig = useTableSearchParams(
    {
      query: searchParams,
      pathname,
      replace: (url) => navigate(url, { replace: true }),
    },
    {
      defaultValues: {
        pagination: {
          pageIndex: 0,
          pageSize: pagination || 10,
        },
      },
    },
  )

  const table = useReactTable({
    ...(pagination || filterType ? urlSyncConfig : {}),
    data,
    columns: [
      ...(rowSelection
        ? [
            {
              id: 'selection',
              header: ({ table }: HeaderContext<TData, TValue>) => (
                <div className='flex items-center'>
                  <Checkbox
                    className='border-muted bg-accent'
                    checked={table.getIsAllPageRowsSelected()}
                    onCheckedChange={() => table.toggleAllPageRowsSelected()}
                  />
                </div>
              ),
              cell: ({ row }: CellContext<TData, TValue>) => (
                <div className='flex items-center'>
                  <Checkbox
                    className='border-muted bg-accent'
                    checked={row.getIsSelected()}
                    onCheckedChange={() => row.toggleSelected()}
                  />
                </div>
              ),
            },
          ]
        : []),
      ...columns,
    ],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
    defaultColumn: {
      size: undefined,
    },
    initialState: {
      columnVisibility: visibilityState || {},
      pagination: pagination ? { pageSize: pagination || 10 } : undefined,
    },
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: filterType || 'auto',
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    enableRowSelection: !!rowSelection,
    onRowSelectionChange: onRowSelectionChange,
    state: {
      ...(pagination || filterType ? urlSyncConfig.state : {}),
      sorting,
      ...(!filterType ? { globalFilter: '' } : {}),
      ...(rowSelection ? { rowSelection } : {}),
    },
    getRowId: getRowId,
  })

  useEffect(() => {
    const rows = table.getRowModel().rows
    onVisibleRowsChange?.(rows.map((row) => row.original))
  }, [
    table.getState().pagination,
    table.getState().globalFilter,
    table.getState().sorting,
    data.length,
  ])

  return (
    <div className={`${className ?? ''} rounded-md border`}>
      {filterType ||
      (pagination && data.length > pagination && pagination > 25) ? (
        <div className='flex items-start justify-between m-3 gap-3 flex-wrap'>
          {filterType ? (
            <Input
              className='w-full max-w-[15rem]'
              value={(table.getState().globalFilter as string) ?? ''}
              onChange={(e) => table.setGlobalFilter(String(e.target.value))}
              placeholder='Search...'
            />
          ) : null}

          {customHeaderActions ? (
            customHeaderActions
          ) : pagination ? (
            <TablePagination
              currentPage={table.getState().pagination.pageIndex + 1}
              totalPages={table.getPageCount()}
              onPageChange={(page) => table.setPageIndex(page - 1)}
              showPreviousNext={true}
            />
          ) : null}
        </div>
      ) : null}

      <Table>
        {includeHeader && (
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        minWidth: header.column.columnDef.size,
                        maxWidth: header.column.columnDef.size,
                        width: header.column.columnDef.size,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={`${
                  onRowClick ? 'cursor-pointer' : ''
                } ${rowClassName?.(row.original)}`}
                data-state={row.getIsSelected() && 'selected'}
                onClick={() => (onRowClick ? onRowClick(row.original) : null)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    className={cellClassName ?? ''}
                    key={cell.id}
                    style={{
                      minWidth: cell.column.columnDef.size,
                      maxWidth: cell.column.columnDef.size,
                      width: cell.column.columnDef.size,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-24 text-center'>
                {emptyMessage ? emptyMessage() : t('shared.no-results')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {pagination && data.length > pagination ? (
        <div className='flex justify-end m-3'>
          <TablePagination
            currentPage={table.getState().pagination.pageIndex + 1}
            totalPages={table.getPageCount()}
            onPageChange={(page) => table.setPageIndex(page - 1)}
            showPreviousNext={true}
          />
        </div>
      ) : null}
    </div>
  )
}

const EmptyMessageWithLoader = ({
  emptyMessageKey,
  isPending,
  isLoadingError,
  error,
  t,
}: {
  emptyMessageKey: string
  isPending: boolean
  isLoadingError: boolean
  error: Error | null
  t: TFunction
}) => {
  if (isPending)
    return (
      <div className='my-8'>
        <PulseLoader
          loading={true}
          speedMultiplier={0.65}
          size={15}
          color='hsl(var(--primary))'
        />
      </div>
    )
  if (isLoadingError && error) {
    const { title, description } = getErrorDetails(error, t)
    return (
      <div className='my-8'>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    )
  } else {
    return <div className='my-8'>{t(emptyMessageKey)}</div>
  }
}

export { DataTable, EmptyMessageWithLoader, TablePagination }
